
import EoImg from '../EoImg/EoImg.vue'

export default {
  name: 'EoDrawer',
  components: { EoImg },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /**
     * Position on the screen: (top) (left) (right) (center)
     */
    position: {
      type: String,
      validator: prop => Object.values(['left', 'right', 'top', 'center', 'bottom']).includes(prop),
      default: 'top',
    },
    /**
     * Is the drawer open or closed
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * Style: background image url for the drawer
     */
    backgroundImage: {
      type: String,
      default: null,
    },
    /**
     * Style: background color of the drawer
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * Style: color of the text
     */
    textColor: {
      type: String,
      default: null,
    },
    /**
     * Style: maximum width on large screens: (1-12 OR pixels)
     */
    maxWidth: {
      type: [String, Number],
      default: '400px',
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
  },
  data () {
    return {
      isOpen: false,
      focusableElements: [],
      elementThatOpenedDrawer: null,
    }
  },
  computed: {
    classes () {
      return {
        'eo-drawer': true,
        ['is-' + this.position]: !!this.position,
      }
    },
    styles () {
      return {
        '--max-width': this.convertWidthToCss(this.maxWidth),
      }
    },
    classesBackground () {
      return {
        'eo-drawer__background': true,
        ['color--' + this.color]: !!this.color,
      }
    },
    classesContent () {
      return {
        'eo-drawer__content': true,
        ['text--' + this.textColor]: !!this.textColor,
      }
    },
    firstFocusableElement () {
      return this.focusableElements[0] || null
    },
    lastFocusableElement () {
      return this.focusableElements[this.focusableElements.length - 1] || null
    },
    backgroundSize () {
      const maxWidth = this.maxWidth.replace('px','')
      switch(this.position) {
        case 'center':
        case 'bottom':
          return `xs:${maxWidth}x449 sm:${maxWidth}x300`
        case 'top':
          return 'xs:414x896 sm:768x1024 md:1280x800 lg:1536x864 xl:1920x1080'
        case 'left':
        case 'right':
        default:
          return `xs:${maxWidth}x896 sm:${maxWidth}x1024 md:${maxWidth}x800 lg:${maxWidth}x864 xl:${maxWidth}x1080`
      }
    },
    backgroundWidth () {
      const maxWidth = this.maxWidth.replace('px','')
      switch(this.position) {
        case 'center':
        case 'bottom':
          return `xs:${maxWidth < 600 ? maxWidth : '100vw'} sm:${maxWidth < 900 ? maxWidth : '100vw'} md:${maxWidth < 1200 ? maxWidth : '100vw'} lg:${maxWidth < 1800 ? maxWidth : '100vw'} xl:${maxWidth >= 1800 ? maxWidth : '100vw'}`
        case 'top':
          return '100vw'
        case 'left':
        case 'right':
        default:
          return `xs:${maxWidth < 600 ? maxWidth : '100vw'} sm:${maxWidth < 900 ? maxWidth : '100vw'} md:${maxWidth < 1200 ? maxWidth : '100vw'} lg:${maxWidth < 1800 ? maxWidth : '100vw'} xl:${maxWidth >= 1800 ? maxWidth : '100vw'}`
      }
    },
  },
  watch: {
    value: { handler: 'toggleAndFocus' , immediate: true },
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeyDown)
  },
  mounted () {
    window.addEventListener('keydown', this.onKeyDown)
    this.focusableElements = this.$refs.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  },
  methods: {
    toggleAndFocus (newState) {
      if (newState === this.isOpen) { return }
      this.isOpen = newState
      this.moveFocus()
    },
    onKeyDown (e) {
      if (e.key === 'Tab' || e.keyCode === 9) {
        this.loopFocus(e)
      }
      if (e.key === 'Escape') {
        this.close()
        this.moveFocus()
      }
    },
    loopFocus (e) {
      /**
       * Emitted when the focus of the user is looped (to cycle focus inside the drawer)
       * @event loop-focus
       */
      if (e.shiftKey && document.activeElement === this.firstFocusableElement) {
        // move focus to the last focusable element
        this.lastFocusableElement?.focus()
        e.preventDefault()
        this.$emit('loop-focus')
      } else if (!e.shiftKey && document.activeElement === this.lastFocusableElement) {
        // move focus to the first focusable element
        this.firstFocusableElement?.focus()
        e.preventDefault()
        this.$emit('loop-focus')
      }
    },
    moveFocus () {
      this.$nextTick(() => {
        if (this.isOpen) {
          this.elementThatOpenedDrawer = document.activeElement
          this.firstFocusableElement?.focus()
          this.$refs.content?.scrollTo(0,0) // prevent jumping due to the focus shift
        } else {
          this.elementThatOpenedDrawer?.focus()
          this.elementThatOpenedDrawer = null
        }
      })
    },
    close () {
      /**
       * Emitted when the overlay is clicked. Closes the drawer
       * @event input
       */
      this.isOpen = false
      this.$emit('input', false)
    },
    convertWidthToCss (size) {
      /**
       * Convert size to pixels when a integer has been given
       * @property {String} size - number from 1 to 12 or a fixed amount of 'px'
       */
      if (!size) return null
      if (Number(size) && 0 < size && size <= 12) {
        return (100 / 12 * size) + '%'
      }
      if (typeof size === 'string' && (size.includes('px') || size.includes('calc'))) {
        return size
      }
      return size + 'px'
    },
  },
}
